import { DateRange } from './../../../interfaces/dateRange';
import { MediaclipService } from 'app/services/mediaclip.service';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { NgForm } from '@angular/forms';
import { BbFilterConfiguration } from '../../../components/shared/bb-filters/bb-filters.component';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, ReplaySubject } from 'rxjs';
import { OverlayService } from './../../../services/overlay.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { CustomField } from '../../../interfaces/publication';
import { StatsReport } from '../../../interfaces/report';
import { SapiService } from '../../../services/sapi.service';
import { FilterSet } from 'app/services/filter-set.service';
import { WebSocketsService } from './../../../services/web-sockets.service';
import { User } from 'app/interfaces/user';


@Component({
	selector: 'report-details-contents',
	templateUrl: './report-details-contents.component.html',
	styleUrls: ['./report-details-contents.component.scss']
})

export class ReportDetailsContentsComponent implements OnInit, OnDestroy, OnChanges {
	@Input() form: NgForm;
	@Input() report: StatsReport;
	@Output() reportChanged: EventEmitter<StatsReport> = new EventEmitter<StatsReport>();

	subscriptions: Subscription;
	dateRangePresets: any[];
	filterConfig: BbFilterConfiguration;
	filterSetChange = new ReplaySubject(1);
	filterSearchResults: any;
	searching: boolean;
	generatingReport: boolean;
	reportUrl: string;
	fileName: string;
	downloadError: boolean;
	emailAddress: string;
	fromDate: Date;
	toDate: Date;

	constructor(
		private translate: TranslateService,
		private authenticationService: AuthenticationService,
		private sapiService: SapiService,
		protected webSocketsService: WebSocketsService,
		private _overlayService: OverlayService,
		private mediaclipService: MediaclipService,
		private _translate: TranslateService
	) {
		this.subscriptions = new Subscription();
		this.dateRangePresets = [
			{ label: this.translate.instant('TODAY'), value: 'today' },
			{ label: this.translate.instant('YESTERDAY'), value: 'yesterday' },
			{ label: this.translate.instant('LAST_X_DAYS', { x: 7 }), value: 'last-7-days' },
			{ label: this.translate.instant('LAST_X_DAYS', { x: 30 }), value: 'last-30-days', default: true },
			{ label: this.translate.instant('LAST_ENTITY', { entity: this.translate.instant('WEEK').toLowerCase() }), value: 'last-week' },
			{ label: this.translate.instant('LAST_ENTITY', { entity: this.translate.instant('MONTH').toLowerCase() }), value: 'last-month' },
			{ label: this.translate.instant('LAST_ENTITY', { entity: this.translate.instant('QUARTER').toLowerCase() }), value: 'last-quarter' },
			{ label: this.translate.instant('LAST_ENTITY', { entity: this.translate.instant('YEAR').toLowerCase() }), value: 'last-year' },
			{ label: this.translate.instant('ENTITY_TO_DATE', { entity: this.translate.instant('WEEK').toLowerCase() }), value: 'week-to-date' },
			{ label: this.translate.instant('ENTITY_TO_DATE', { entity: this.translate.instant('MONTH').toLowerCase() }), value: 'month-to-date' },
			{ label: this.translate.instant('ENTITY_TO_DATE', { entity: this.translate.instant('YEAR').toLowerCase() }), value: 'year-to-date' },
			{ label: this.translate.instant('CUSTOM_DATE_RANGE'), value: 'custom' }
		];

		this.filterConfig = {
			search: true,
			filters: this.mediaclipService.getFilterConfigOptions()
		};

		this.filterConfig.filters.forEach((filter) => {
			filter.entity = 'mediaclip';
		});

		const statsFields = {
			label: this.translate.instant('STATISTICS'),
			fields: {
				country: {
					label: this.translate.instant('COUNTRY'),
					fieldType: 'singleSelect'
				},
				region: {
					label: this.translate.instant('REGION'),
					fieldType: 'singleSelect'
				},
				city: {
					label: this.translate.instant('CITY'),
					fieldType: 'singleSelect'
				},
				custom_stats: {
					label: this.translate.instant('CUSTOM_STATS_EVENT'),
					fieldType: 'multiSelect'
				},
				quartiles: {
					label: this.translate.instant('VAST_QUARTILES'),
					fieldType: 'multiSelect'
				},
				mainContentStarted: {
					label: this.translate.instant('MAIN_CONTENT_STARTED'),
					fieldType: 'boolean'
				},
				creativeStart: {
					label: this.translate.instant('WITH_AD_IMPRESSION'),
					fieldType: 'boolean'
				},
				// vmap_impression: {
				// 	label: this.translate.instant('WITH_VMAP_IMPRESSION'),
				// 	fieldType: 'boolean'
				// },
				// preroll_impression: {
				// 	label: this.translate.instant('WITH_PREROLL_IMPRESSION'),
				// 	fieldType: 'boolean'
				// },
				// postroll_impression: {
				// 	label: this.translate.instant('WITH_POSTROLL_IMPRESSION'),
				// 	fieldType: 'boolean'
				// },
				playout: {
					label: this.translate.instant('PLAYOUT'),
					fieldType: 'singleSelect'
				},
				domain: {
					label: this.translate.instant('WEB_DOMAIN'),
					fieldType: 'text'
				},
				referrer: {
					label: this.translate.instant('WEB_ADDRESS'),
					fieldType: 'text'
				},
				rdomain: {
					label: this.translate.instant('REAL_REFERRER_DOMAIN'),
					fieldType: 'text'
				},
				rreferrer: {
					label: this.translate.instant('REAL_REFERRER_URL'),
					fieldType: 'text'
				},
				browserName: {
					label: this.translate.instant('BROWSERNAME'),
					fieldType: 'singleSelect'
				},
				browserVersion: {
					label: this.translate.instant('BROWSERVERSION'),
					fieldType: 'singleSelect'
				},
				osName: {
					label: this.translate.instant('OSNAME'),
					fieldType: 'singleSelect'
				},
				osVersion: {
					label: this.translate.instant('OSVERSION'),
					fieldType: 'singleSelect'
				},
				bitrate: {
					label: this.translate.instant('BITRATE_IN_KBPS'),
					fieldType: 'number'
				},
				resolution: {
					label: this.translate.instant('RESOLUTION'),
					fieldType: 'singleSelect'
				},
				playmode: {
					label: this.translate.instant('PLAYMODE'),
					fieldType: 'singleSelect'
				},
				autoPlay: {
					label: this.translate.instant('AUTOPLAY'),
					fieldType: 'boolean'
				},
				autoMute: {
					label: this.translate.instant('AUTOMUTE'),
					fieldType: 'boolean'
				},
				fullscreen: {
					label: this.translate.instant('FULLSCREEN'),
					fieldType: 'boolean'
				},
				deviceType: {
					label: this.translate.instant('DEVICE_TYPE'),
					fieldType: 'singleSelect'
				},
				mobileBrand: {
					label: this.translate.instant('MOBILE_BRAND'),
					fieldType: 'singleSelect'
				},
				mobileModel: {
					label: this.translate.instant('MOBILE_MODEL'),
					fieldType: 'text'
				}
			}
		};

		this.subscriptions.add(this.authenticationService.getAllowedCustomFields().subscribe((fields: CustomField[]) => {
			fields.forEach((field: CustomField) => {

				// map custom field type to filter type
				let filterType: string;

				switch (field.type) {
					case 'text':
						filterType = 'text';
						break;
					case 'int':
					case 'float':
						filterType = 'number';
						break;
					case 'date':
						filterType = 'date';
						break;
					case 'boolean':
						filterType = 'boolean';
						break;
					case 'string':
						filterType = 'select';
						break;
					case 'multistring':
						filterType = 'customMultiSelect';
						break;
				}

				// for now, 'color' and 'relation' are ignored

				if (filterType) {
					this.filterConfig.filters.push({
						entity: 'mediaclip',
						property: field.name,
						label: field.label,
						type: filterType
					});
				}
			});
			// add custom stats fields
			fields.forEach((field: CustomField) => {
				if (field.includeInStats) {
					this.filterConfig.filters.push({
						entity: 'stats',
						property: field.name,
						label: 'Stats: ' + field.label,
						type: field.type
					});
				}
			});

			// add stats field after the custom fields
			for (const fieldName in statsFields.fields) {
				if (statsFields.fields[fieldName]) {
					this.filterConfig.filters.push({
						entity: 'stats',
						property: fieldName,
						label: 'Stats: ' + statsFields.fields[fieldName].label,
						type: statsFields.fields[fieldName].fieldType
					});
				}
			}
		}));
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.report) {
			if (this.report.fixedDateRangeFrom) {
				this.fromDate = new Date(this.report.fixedDateRangeFrom);
			} else {
				this.fromDate = new Date();
				this.fromDate.setDate(this.fromDate.getDate() - 30);
			}
			if (this.report.fixedDateRangeTo) {
				this.toDate = new Date(this.report.fixedDateRangeTo);
			} else {
				this.toDate = new Date();
			}
		}
	}

	ngOnInit() {
		this.reportChanged.emit(this.report);
		this.searching = false;
		this.generatingReport = false;
		this.downloadError = false;

		this.authenticationService.getUser().subscribe((user: User) => {
			if (user && user.email && this.report) {
				this.emailAddress = user.email;
				if (!this.report.recipients) {
					this.report.recipients = [];
					this.report.recipients.push(user.email);
				}
			}
		});
	}

	onDateRangeSelected(dateRange: DateRange) {
		const startDateJson = dateRange.startDate.toJSON();
		const endDateJson = dateRange.endDate.toJSON();
		if (this.report.fixedDateRangeFrom !== startDateJson || this.report.fixedDateRangeTo !== endDateJson) {
			this.form.form.markAsDirty();
		}
		this.report.fixedDateRangeFrom = startDateJson;
		this.report.fixedDateRangeTo = endDateJson;
	}

	onFilterSetChange(filterSet: FilterSet) {
		this.searching = true;
		this.filterSetChange.next(filterSet);
	}

	dateRangePresetChanged(value: string) {
		if (this.report) {
			this.report.dynamicDateRange = value;
			if (value === 'custom') {
				this.report.dateRangeType = 'fixed';
			} else {
				this.report.dateRangeType = 'dynamic';
			}
			this.dateRangeTypeChanged(this.report.dateRangeType);
		}
	}

	dateRangeTypeChanged(value: string) {

		if (value === 'fixed' && !this.report.fixedDateRangeFrom && !this.report.fixedDateRangeTo) {

			const lastweek = new Date();
			lastweek.setMilliseconds(0);
			lastweek.setSeconds(0);
			lastweek.setMinutes(0);
			lastweek.setHours(0);
			lastweek.setDate(lastweek.getDate() - 7);

			this.report.fixedDateRangeFrom = lastweek.toISOString();

			// defaults
			const today = new Date();
			today.setMilliseconds(0);
			today.setSeconds(0);
			today.setMinutes(0);
			today.setHours(0);

			this.report.fixedDateRangeTo = new Date(today.getTime() - 1).toISOString();
		}
	}

	onGenerateDownloadClick() {
		this.webSocketsService.addBbNotification(`advancedreportsDownload${this.report.id}`);
		this.generatingReport = true;
		this.subscriptions.add(this.webSocketsService.subscribeToChannel(`advancedreportsDownload${this.report.id}`).subscribe((websocketEvent: any) => {
			if (websocketEvent && websocketEvent.ev) {
				if (websocketEvent.ev === 'finish') {
					if (websocketEvent.status === 'error') {
						const msg = this._translate.instant('REPORT_ERROR_GENERATING');
						this._overlayService.createToast(msg, 'info-circle');
						this.downloadError = true;
					}
					this.webSocketsService.unsubscribeFromChannel(`advancedreportsDownload${this.report.id}`);
					this.generatingReport = false;
				}
			}
		}));

		this.sapiService.getUrlFromBase('/sapi/report/' + this.report.id + '/download?email=' + this.emailAddress).subscribe((r: any) => {
			if (r.error === false) {
				const msg = this._translate.instant('REPORT_IS_BEING_GENERATED');
				this._overlayService.createToast(msg, 'info-circle');
				this.downloadError = false;
			} else {
				const msg = this._translate.instant('REPORT_ERROR_GENERATING');
				this._overlayService.createToast(msg, 'info-circle');
				this.downloadError = true;
			}
		});
	}

	onColumnsChanged(report: StatsReport) {
		this.reportChanged.emit(report);
	}

	onScheduleClick() {
		if (!this.report.generateInterval || this.report.generateInterval === 'manually') {
			this.report.generateInterval = 'weekly';
			this.report.generateIntervalDays = ['monday'];
			this.form.form.markAsDirty();
		}
	}

	ngOnDestroy() {
		this.webSocketsService.unsubscribeFromChannel(`advancedreportsDownload${this.report.id}`);
		this.subscriptions.unsubscribe();
	}
}
